import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthProvider';

/**
 * Whether this person has been shown the walkthrough, and how to say they have.
 *
 * ONCE PER PERSON, NOT ONCE PER BROWSER. `localStorage` would have been a line of code, but it
 * would mean the tour returning on a second machine, after a profile reset, or in a private
 * window — for somebody who has already sat through it. The flag therefore lives in
 * `userPreferences/{uid}`, which follows the person.
 *
 * localStorage is still used, as a CACHE in front of it. Firestore takes a moment to answer, and
 * a walkthrough that appears half a second after the page settles — every time, for people who
 * have already dismissed it — is worse than one that never appeared. The local value suppresses
 * that flash; the stored value is what actually decides.
 *
 * FAILURE IS SILENT AND FAVOURS NOT INTERRUPTING. If the read fails, nothing is shown: somebody
 * trying to open a matter should not be handed a tour because a lookup went wrong. If the write
 * fails, the local cache still stops it reappearing in this browser, and the worst case is that
 * it greets them once more elsewhere.
 */

const LOCAL_KEY = 'nmi.walkthrough.seen';

const readLocal = () => {
  try {
    return window.localStorage.getItem(LOCAL_KEY) === 'yes';
  } catch {
    // Private windows and locked-down browsers throw on access rather than returning null.
    return false;
  }
};

const writeLocal = () => {
  try {
    window.localStorage.setItem(LOCAL_KEY, 'yes');
  } catch {
    /* nothing to do: the stored flag is the one that counts */
  }
};

export function useWalkthroughState() {
  const { user } = useAuth();
  const uid = user && user.uid;

  // `null` means "not known yet" and is deliberately distinct from `false`. Nothing is shown
  // until the answer is in, so the tour never flashes up and then vanishes.
  const [seen, setSeen] = useState(() => (readLocal() ? true : null));

  useEffect(() => {
    if (!uid) return undefined;
    if (readLocal()) {
      setSeen(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'userPreferences', uid));
        if (cancelled) return;
        const stored = snap.exists() && snap.data().walkthroughSeenAt;
        if (stored) writeLocal();
        setSeen(Boolean(stored));
      } catch {
        // Treated as "already seen": the failure mode of an unexpected tour is worse than the
        // failure mode of a missing one, which the Help page can always put right.
        if (!cancelled) setSeen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  /** Records that they have been through it. Called when the tour is finished OR skipped. */
  const markSeen = useCallback(async () => {
    writeLocal();
    setSeen(true);
    if (!uid) return;
    try {
      await setDoc(
        doc(db, 'userPreferences', uid),
        { walkthroughSeenAt: serverTimestamp(), walkthroughVersion: 1 },
        { merge: true }
      );
    } catch {
      /* the local cache carries it for this browser */
    }
  }, [uid]);

  return { seen, markSeen };
}
